import React, { useEffect, useState } from "react";
import EcnButtonFilledV2 from "../components/StyledComponents/EcnButtonFilledV2";
import EcnButton from "../components/StyledComponents/EcnButton";
import { Icon } from "@iconify/react";
import { useAppDispatch, useAppSelector } from "../redux/hooks";
import { useNavigate, useParams } from "react-router-dom";
import { getJobDetail } from "../redux/slices/jobSlices/getJobDetailSlice";
import {
  Dialog,
} from "@mui/material";

import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import ShareJobDialog from "../components/JobDetail/ShareJobDialog";
import NotFound from "./NotFound";
import Loader from "../components/UI/Loader";
import { userDetails } from "../redux/slices/userSlices/userDetailsSlice";
import { getCompanyJobs } from "../redux/slices/jobSlices/getCompanyJobsSlice";
import moment from "moment";
import { getCompanyDetail } from "../redux/slices/jobSlices/getCompanyDetailSlice";

const GATEWAY_BASE = process.env.REACT_APP_API_GATEWAY_BASE;
const ADMIN_BASE =
  GATEWAY_BASE === "https://api.dev.ecndev.io"
    ? "https://admin.dev.ecndev.io"
    : "https://admin.onefinnet.com";

const JobDetail = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { jobId, companyName } = useParams<{
    jobId: string;
    companyName: string;
  }>();
  const { userInfo } = useAppSelector((state) => state.userDetails);
  const { userInfo: user } = useAppSelector((state: any) => state.userDetails);
  const { jobList } = useAppSelector((state) => state.getCompanyJobs);
  const { jobData, loading } = useAppSelector((state) => state.getJobDetails);
  const [shareJobOpen, setShareJobOpen] = useState(false);
  const [showPromo, setShowPromo] = useState(false);
  const { companyDetails } = useAppSelector((state) => state.getCompanyDetails);

  // style state
  const [color, setColor] = useState("#0034BB");
  const [radius, setRadius] = useState("14px");
  const [fontStyle, setFontStyle] = useState("Plus Jakarta Sans");
  const [buttonRadius, setButtonRadius] = useState("28");
  const [showWaterMark, setShowWaterMark] = useState(true);

  useEffect(() => {
    if (companyName) {
      dispatch(
        getCompanyJobs({
          skip: 0 * 6,
          limit: 5,
          firm: companyName,
          userId: userInfo?._id,
        })
      );
      dispatch(getCompanyDetail(companyName));
    }
  }, [companyName, userInfo, dispatch]);

  useEffect(() => {
    if (companyDetails) {
      const computedRadius = companyDetails?.border_radius
        ? `${companyDetails?.border_radius}`
        : "14px";
      const computedColor = companyDetails?.color || "#0034BB";
      const computedFontStyle = companyDetails?.font_style || "Plus Jakarta Sans";
      const radiusVal = parseFloat(computedRadius);
      const computedButtonRadius = radiusVal < 12
        ? `${Math.floor(radiusVal / 2)}`
        : `${Math.floor(radiusVal * 2)}`;

      setShowWaterMark(companyDetails?.show_watermark ?? true);
      setRadius(computedRadius);
      setColor(computedColor);
      setFontStyle(computedFontStyle);
      setButtonRadius(computedButtonRadius);
    }
  }, [companyDetails]);

  useEffect(() => {
    if (!user) {
      dispatch(userDetails());
    }
  }, [user, dispatch]);

  useEffect(() => {
    if (jobId) dispatch(getJobDetail({ jobId: jobId }));
  }, [jobId, dispatch]);

  const applyHandler = () => {
    navigate("apply");
  };

  return (
    <>
      {loading ? (
        <div className="loader-container">
          <Loader color={color} />
        </div>
      ) : jobData ? (
        <div className="job-detailV2" style={{ fontFamily: fontStyle }}>
          <div className="heading">
            <EcnButton
              color="secondary"
              startIcon={<Icon icon="mingcute:left-line" />}
              onClick={() => {
                navigate(`/${companyName}`);
              }}
            >
              See all jobs
            </EcnButton>
            <div className="icon"></div>
          </div>
          <div className="body-container">
            <div className="left-container" style={{ borderRadius: radius }}>
              <div className="job-card">
                <div className="job-title">
                  <div className="company">
                    <img
                      className="firm-logo"
                      src={jobData?.display_picture}
                      alt=""
                      style={{ borderRadius: radius }}
                    />{" "}
                    <div className="company-right">
                      <h1>{jobData?.job_title}</h1>
                      <p>{jobData?.organisations?.[0]}</p>
                    </div>
                  </div>
                  <div className="apply">
                    <div
                      className="share-btn"
                      style={{ borderRadius: radius }}
                      onClick={() => setShareJobOpen(true)}
                    >
                      <Icon
                        icon={"mingcute:share-forward-line"}
                        width={24}
                        height={24}
                      />
                    </div>
                    {jobData?.closed ? (
                      <EcnButtonFilledV2
                        variant="contained"
                        sx={{ width: "200px", borderRadius: buttonRadius }}
                        disabled
                        className="btn-contained apply-btn closed"
                      >
                        Closed
                      </EcnButtonFilledV2>
                    ) : jobData?.application ? (
                      <EcnButtonFilledV2
                        variant="contained"
                        disabled
                        className="btn-contained apply-btn"
                        sx={{
                          cursor: "not-allowed",
                          width: "191px",
                          borderRadius: buttonRadius,
                        }}
                      >
                        Applied
                      </EcnButtonFilledV2>
                    ) : (
                      <EcnButtonFilledV2
                        variant="contained"
                        className="apply-btn"
                        onClick={applyHandler}
                        sx={{ width: "200px", borderRadius: buttonRadius }}
                      >
                        Apply for this job
                      </EcnButtonFilledV2>
                    )}

                    <ShareJobDialog
                      link={`https://jobs.onefinnet.com/${companyName}/job/${jobId}`}
                      open={shareJobOpen}
                      setOpen={setShareJobOpen}
                    />
                  </div>
                </div>
                <div className="job-details">
                  <div className="container-body">
                    <div className="container-section">
                      <div
                        className="container"
                        style={{ borderRadius: radius }}
                      >
                        <Icon
                          icon={"mingcute:necktie-line"}
                          height={16}
                          width={16}
                          color={color}
                        />
                        {jobData?.job_roles?.[0]}
                      </div>
                      <div
                        className="container"
                        style={{ borderRadius: buttonRadius }}
                      >
                        <Icon
                          icon={"mingcute:suitcase-line"}
                          height={16}
                          width={16}
                          color={color}
                        />
                        {jobData?.role_type}
                      </div>
                      <div
                        className="container"
                        style={{ borderRadius: radius }}
                      >
                        <Icon
                          icon={"mingcute:time-line"}
                          height={16}
                          width={16}
                          color={color}
                        />
                        {jobData?.max_experience_required !== 100
                          ? jobData?.experience_required +
                          " - " +
                          jobData?.max_experience_required
                          : jobData?.experience_required + "+"}{" "}
                        years of Experience
                      </div>
                    </div>
                    <div className="container-section">
                      <div
                        className="container"
                        style={{ borderRadius: radius }}
                      >
                        <Icon
                          icon={"mingcute:building-2-line"}
                          height={16}
                          width={16}
                          color={color}
                        />
                        {jobData?.industries?.[0]}
                      </div>
                       {jobData?.address?.cities?.length > 0 && jobData?.address?.cities?.[0] && (
  <div className="container" style={{ borderRadius: radius }}>
    <Icon
      icon="mingcute:location-line"
      height={16}
      width={16}
      color={color}
    />
    {jobData.address.cities[0]}
  </div>
)}
                      <div
                        className="container"
                        style={{ borderRadius: radius }}
                      >
                        <Icon
                          icon={"mingcute:tag-2-line"}
                          height={16}
                          width={16}
                          color={color}
                        />
                        {jobData?.work_type}
                      </div>
                    </div>
                  </div>

                  <div className="posted">
                    {moment.unix(jobData?.createdAt).fromNow()}
                    {!(jobData?.application_deadline === 100 ||
                      jobData?.application_deadline === -1) &&
                      (<>
                        | Apply before{" "}
                        {
                          moment.unix(jobData?.application_deadline).format("ll")
                        }
                      </>)
                    }
                  </div>
                </div>
              </div>
              {jobData?.description && (
                <div className="description">
                  <p className="desc-heading">Job Description</p>
                  <div
                    className="description-container"
                    dangerouslySetInnerHTML={{
                      __html: jobData?.description,
                    }}
                  />
                </div>
              )}
              {jobData?.closed ? (
                <EcnButtonFilledV2
                  variant="contained"
                  disabled
                  sx={{ width: "200px", borderRadius: buttonRadius }}
                  className="btn-contained apply-btn closed"
                >
                  Closed
                </EcnButtonFilledV2>
              ) : jobData?.application ? (
                <EcnButtonFilledV2
                  variant="contained"
                  disabled
                  className="btn-contained apply-btn"
                  sx={{
                    cursor: "not-allowed",
                    width: "191px",
                    borderRadius: buttonRadius,
                  }}
                >
                  Applied
                </EcnButtonFilledV2>
              ) : (
                <EcnButtonFilledV2
                  variant="contained"
                  className="apply-btn-bottom"
                  onClick={applyHandler}
                  sx={{ width: "200px", borderRadius: buttonRadius }}
                >
                  Apply for this job
                </EcnButtonFilledV2>
              )}
            </div>
            <div className="right-container">
              <div className="about-company" style={{ borderRadius: radius }}>
                <div className="title">
                  <img
                    className="firm-logo"
                    src={jobData?.display_picture}
                    alt=""
                  />{" "}
                  <h1>{jobData?.organisations?.[0]}</h1>
                </div>

                <div className="details">
                  <h2>About The Company</h2>
                  <ReactMarkdown
                    className="markdown-data"
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[rehypeRaw]}
                  >
                    {jobData?.firm_overview}
                  </ReactMarkdown>
                </div>
                <div
                  className="explore"
                  style={{ borderRadius: radius }}
                  onClick={() => {
                    window.open(jobData?.website_link, "_blank");
                  }}
                >
                  <p>Know More</p>{" "}
                  <Icon
                    icon={"mingcute:right-line"}
                    width={20}
                    height={20}
                    color="#1D2939"
                  />
                </div>
              </div>
              <div className="similar-jobs">
                {jobList?.total_count !== 1 && (
                  <p className="title">Similar Jobs</p>
                )}
                {jobList?.jobs?.map((job: any) => {
                  if (job._id === jobId) return null;
                  return (
                    <div className="card" style={{ borderRadius: radius }}>
                      <p className="title">{job.job_title}</p>
                      <div className="details">
                        <div className="column">
                          <Icon
                            icon={"mingcute:suitcase-line"}
                            width={16}
                            height={16}
                            color={color}
                          />
                          <p>{job.industries?.[0]}</p>
                        </div>

                        <div className="column">
                          <Icon
                            icon={"mingcute:time-line"}
                            width={16}
                            height={16}
                            color={color}
                          />
                          <p>{job.role_type}</p>
                        </div>

{jobData?.address?.cities?.length > 0 && jobData?.address?.cities?.[0] && (
  <div className="container" style={{ borderRadius: radius }}>
    <Icon
      icon="mingcute:location-line"
      height={16}
      width={16}
      color={color}
    />
    {jobData.address.cities[0]}
  </div>
)}
                      </div>
                      <div
                        className="explore"
                        style={{ borderRadius: radius }}
                        onClick={() => {
                          navigate(`/${companyName}/job/${job?._id}`);
                        }}
                      >
                        <p>Explore this job</p>{" "}
                        <Icon
                          icon={"mingcute:right-line"}
                          width={20}
                          height={20}
                          color="#1D2939"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          {showWaterMark && (
            !showPromo ? (
              <div
                className="floating-container"
                onClick={() => setShowPromo(true)}
              >
                <p>Created in</p>
                <img src="/logo.svg" alt="Elevate 2.0 icon" />
              </div>
            ) : (
              <Dialog
                open={showPromo}
                onClose={() => setShowPromo(false)}
                maxWidth="md"
                className="promo-dialog"
                PaperProps={{
                  sx: {
                    position: "absolute",
                    bottom: "10px",
                    right: "10px",
                    borderRadius: buttonRadius,
                    fontFamily: fontStyle,
                    m: 0,
                  },
                }}
                slotProps={{
                  backdrop: {
                    style: {
                      boxShadow: "none",
                      backgroundColor: "transparent",
                    },
                  },
                }}
              >
                <div
                  className="promo-dialog-container"
                  style={{ borderRadius: radius }}
                >
                  <div>
                    <img
                      src="https://ecn.blob.core.windows.net/ecn/images/talent ad.png"
                      alt=""
                      className="ad-img"
                    />
                  </div>
                  <div className="text-container">
                    <h1>Post a Job today</h1>
                    <p>
                      With Onefinnet Talent, you can quickly post a job and find
                      the right candidates.
                    </p>
                  </div>
                  <EcnButtonFilledV2
                    className="featured-button"
                    sx={{ width: "200px", borderRadius: buttonRadius }}
                    onClick={() => {
                      window.open(
                        ADMIN_BASE + "/signup?client=third-party&cupoun=FINFIRST",
                        "_blank"
                      );
                    }}
                  >
                    Post a Featured Job
                  </EcnButtonFilledV2>
                  <EcnButtonFilledV2
                    className="try-button"
                    sx={{ width: "200px", borderRadius: buttonRadius }}
                    onClick={() => {
                      window.open(ADMIN_BASE + "/signup", "_blank");
                    }}
                  >
                    Try Talent for Free
                  </EcnButtonFilledV2>
                </div>
              </Dialog>
            )
          )}
        </div>
      ) : (
        <NotFound />
      )}
    </>
  );
};

export default JobDetail;
